import PropTypes from 'prop-types';
import React from 'react';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

function onlyDigits(s) {
  return String(s || '')
    .replace(/\D/g, '')
    .slice(0, 6);
}

export function EmailVerificationScreen({
  email,
  loginId,
  onVerify,
  onResend,
  onGoLogin,
  onVerifiedSuccess, // ✅ NEW
  cooldownSeconds = 60,
}) {
  const [code, setCode] = React.useState('');
  const [loadingVerify, setLoadingVerify] = React.useState(false);
  const [loadingResend, setLoadingResend] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [ok, setOk] = React.useState(null);

  // ✅ resend cooldown
  const [cooldown, setCooldown] = React.useState(0);
  const hasLoginId = String(loginId || '').trim().length > 0;

  const canVerify = hasLoginId && code.length === 6 && !loadingVerify && !loadingResend;
  const canResend = hasLoginId && cooldown === 0 && !loadingVerify && !loadingResend;
  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  async function handleVerify() {
    setErr(null);
    setOk(null);
    const c = onlyDigits(code);
    if (c.length !== 6) return setErr('Lütfen 6 haneli kodu gir.');
    if (!hasLoginId)
      return setErr('LoginId boş geldi. Lütfen giriş ekranına dönüp tekrar deneyin.');

    if (c.length !== 6) return setErr('Lütfen 6 haneli kodu gir.');
    setLoadingVerify(true);
    try {
      await onVerify({ loginId, code: c });
      // ✅ verify başarılı -> otomatik giriş
      if (onVerifiedSuccess) {
        await onVerifiedSuccess();
        return; // yönlendirme SignIn tarafında olacağı için burada kalma
      }
      setOk('Doğrulama başarılı. Artık giriş yapabilirsin.');
    } catch (e) {
      const msg = e?.errors
        ? Object.values(e.errors).join(' • ')
        : e?.message || 'Kod doğrulanamadı.';
      setErr(msg);
    } finally {
      setLoadingVerify(false);
    }
  }

  // EmailVerificationScreen.jsx (sadece handleResend içi değişiyor)
  async function handleResend() {
    setErr(null);
    setOk(null);
    setLoadingResend(true);
    if (!hasLoginId)
      return setErr('LoginId boş geldi. Lütfen giriş ekranına dönüp tekrar deneyin.');

    try {
      await onResend({ loginId });
      const shown = email || 'e-posta adresine';
      setOk(`Yeni kod ${shown} gönderildi. E-postanı kontrol et.`);
      setCooldown(cooldownSeconds);
    } catch (e) {
      const msg = e?.errors
        ? Object.values(e.errors).join(' • ')
        : e?.message || 'Kod tekrar gönderilemedi.';
      setErr(msg);
    } finally {
      setLoadingResend(false);
    }
  }

  return (
    <MDBox sx={{ display: 'grid', gap: 1.5 }}>
      <MDBox
        sx={{
          border: '1px solid rgba(0,0,0,0.08)',
          background: 'rgba(255,255,255,0.72)',
          borderRadius: '16px',
          padding: 2,
        }}
      >
        <MDTypography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
          E-postanı doğrula
        </MDTypography>

        <MDTypography variant="button" color="text" sx={{ opacity: 0.8, display: 'block' }}>
          E-postana 6 haneli doğrulama kodu gönderdik.
        </MDTypography>

        <MDBox sx={{ mt: 1 }}>
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.75 }}>
            Gönderilen adres
          </MDTypography>
          <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ display: 'block' }}>
            {email || '—'}
          </MDTypography>
        </MDBox>

        <MDBox sx={{ mt: 1.5 }}>
          <MDInput
            label="Doğrulama kodu"
            value={code}
            onChange={(e) => setCode(onlyDigits(e.target.value))}
            placeholder="000000"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 6,
              style: { letterSpacing: '0.35em', textAlign: 'center', fontWeight: 800 },
            }}
            fullWidth
          />
          <MDTypography
            variant="caption"
            color="text"
            sx={{ opacity: 0.7, mt: 0.75, display: 'block' }}
          >
            Kod gelmediyse “Spam / Gereksiz” klasörünü kontrol et.
          </MDTypography>
        </MDBox>
      </MDBox>

      {err && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {err}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {ok}
        </Alert>
      )}

      <MDButton
        variant="gradient"
        color="info"
        fullWidth
        disabled={!canVerify}
        onClick={handleVerify}
        sx={{ py: 1.2 }}
      >
        {loadingVerify ? (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
            <CircularProgress size={16} thickness={5} />
            <span>Doğrulanıyor…</span>
          </MDBox>
        ) : (
          'Doğrula'
        )}
      </MDButton>

      <MDButton
        variant="outlined"
        color="info"
        fullWidth
        disabled={!canResend}
        onClick={handleResend}
        sx={{ py: 1.1 }}
      >
        {loadingResend ? (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
            <CircularProgress size={16} thickness={5} />
            <span>Gönderiliyor…</span>
          </MDBox>
        ) : cooldown > 0 ? (
          `Tekrar gönder (${cooldown}s)`
        ) : (
          'Kod tekrar gönder'
        )}
      </MDButton>

      <MDButton variant="text" color="dark" fullWidth onClick={onGoLogin} sx={{ py: 1.05 }}>
        Giriş ekranına dön
      </MDButton>
    </MDBox>
  );
}

EmailVerificationScreen.propTypes = {
  email: PropTypes.string,
  loginId: PropTypes.string.isRequired,
  onVerify: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onGoLogin: PropTypes.func.isRequired,
  cooldownSeconds: PropTypes.number,
  onVerifiedSuccess: PropTypes.func,
};
